import React, { useState, useEffect } from 'react';
import { FileText, Download, Printer, Plus, Users, TrendingUp, Edit2, Trash2, Save, X, Eye, Moon, Sun } from 'lucide-react';
import { 
  fetchPurchaseOrders, 
  savePurchaseOrder, 
  deletePurchaseOrder as dbDeletePO,
  fetchVendors, 
  saveVendor, 
  deleteVendor as dbDeleteVendor,
  fetchProjects, 
  saveProject, 
  deleteProject as dbDeleteProject,
  fetchCompanyProfile,
  saveCompanyProfile as dbSaveCompanyProfile
} from './database';

const genBMAP = (type) => `BMAP-${type}-${Date.now().toString().slice(-6)}${Math.random().toString(36).slice(-4).toUpperCase()}`;

/* ================= HELPER FUNCTIONS ================= */
const calculateItemTotal = (item) => {
  const subtotal = item.quantity * item.rate;
  return subtotal + (subtotal * item.gst) / 100;
};

const calculateTotals = (items) => {
  let subtotal = 0;
  let totalGST = 0;
  items.forEach(item => {
    const s = item.quantity * item.rate;
    subtotal += s;
    totalGST += (s * item.gst) / 100;
  });
  return { subtotal, totalGST, grandTotal: subtotal + totalGST };
};

/* ================= COMPANY EDITOR MODAL ================= */
const CompanyEditorModal = ({ 
  tempCompanyProfile, 
  setTempCompanyProfile, 
  setShowCompanyEditor, 
  saveCompanyProfile,
  darkMode 
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} rounded-xl shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Edit Company Profile</h2>
        <button onClick={() => setShowCompanyEditor(false)} className={darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}>
          <X size={24} />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : ''}`}>Company Name</label>
          <input
            type="text"
            value={tempCompanyProfile.name}
            onChange={(e) => setTempCompanyProfile({...tempCompanyProfile, name: e.target.value})}
            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
          />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : ''}`}>Address</label>
          <textarea
            value={tempCompanyProfile.address}
            onChange={(e) => setTempCompanyProfile({...tempCompanyProfile, address: e.target.value})}
            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            rows="3"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : ''}`}>Phone</label>
            <input
              type="text"
              value={tempCompanyProfile.phone}
              onChange={(e) => setTempCompanyProfile({...tempCompanyProfile, phone: e.target.value})}
              className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : ''}`}>Email</label>
            <input
              type="email"
              value={tempCompanyProfile.email}
              onChange={(e) => setTempCompanyProfile({...tempCompanyProfile, email: e.target.value})}
              className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : ''}`}>GST Number</label>
            <input
              type="text"
              value={tempCompanyProfile.gst}
              onChange={(e) => setTempCompanyProfile({...tempCompanyProfile, gst: e.target.value})}
              className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : ''}`}>PAN Number</label>
            <input
              type="text"
              value={tempCompanyProfile.pan}
              onChange={(e) => setTempCompanyProfile({...tempCompanyProfile, pan: e.target.value})}
              className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={() => setShowCompanyEditor(false)}
          className={`px-4 py-2 border rounded ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'hover:bg-gray-50'}`}
        >
          Cancel
        </button>
        <button
          onClick={saveCompanyProfile}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <Save size={18} />
          Save Changes
        </button>
      </div>
    </div>
  </div>
);

/* ================= VENDOR FORM MODAL ================= */
const VendorFormModal = ({ 
  editingVendor, 
  vendorForm, 
  setVendorForm, 
  setShowVendorForm, 
  handleSaveVendor 
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</h2>
        <button onClick={() => setShowVendorForm(false)} className="text-gray-500 hover:text-gray-700">
          <X size={24} />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Vendor Name</label>
          <input
            type="text"
            value={vendorForm.name}
            onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Contact Person</label>
            <input
              type="text"
              value={vendorForm.contact}
              onChange={(e) => setVendorForm({...vendorForm, contact: e.target.value})}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input
              type="text"
              value={vendorForm.phone}
              onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={vendorForm.email}
              onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">GST Number</label>
            <input
              type="text"
              value={vendorForm.gst}
              onChange={(e) => setVendorForm({...vendorForm, gst: e.target.value})}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Address</label>
          <textarea
            value={vendorForm.address}
            onChange={(e) => setVendorForm({...vendorForm, address: e.target.value})}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
            rows="3"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={() => setShowVendorForm(false)}
          className="px-4 py-2 border rounded hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveVendor}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <Save size={18} />
          {editingVendor ? 'Update' : 'Add'} Vendor
        </button>
      </div>
    </div>
  </div>
);

/* ================= PROJECT FORM MODAL ================= */
const ProjectFormModal = ({ 
  editingProject, 
  projectForm, 
  setProjectForm, 
  setShowProjectForm, 
  handleSaveProject 
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
        <button onClick={() => setShowProjectForm(false)} className="text-gray-500 hover:text-gray-700">
          <X size={24} />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Project Name</label>
          <input
            type="text"
            value={projectForm.name}
            onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Project Code</label>
            <input
              type="text"
              value={projectForm.code}
              onChange={(e) => setProjectForm({...projectForm, code: e.target.value})}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Budget (₹)</label>
            <input
              type="number"
              value={projectForm.budget}
              onChange={(e) => setProjectForm({...projectForm, budget: parseFloat(e.target.value) || 0})}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Spent (₹)</label>
            <input
              type="number"
              value={projectForm.spent}
              onChange={(e) => setProjectForm({...projectForm, spent: parseFloat(e.target.value) || 0})}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={() => setShowProjectForm(false)}
          className="px-4 py-2 border rounded hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveProject}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <Save size={18} />
          {editingProject ? 'Update' : 'Add'} Project
        </button>
      </div>
    </div>
  </div>
);

/* ================= PO FORM VIEW ================= */
const POFormView = ({ 
  poForm, 
  setPOForm, 
  vendors, 
  projects, 
  editingPO, 
  setShowPOForm, 
  setEditingPO, 
  savePO,
  addItem,
  removeItem
}) => {
  const totals = calculateTotals(poForm.items);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[95vw] lg:max-w-7xl p-6 my-8 max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6 sticky top-0 bg-white pb-4 border-b">
          <h2 className="text-2xl font-bold">{editingPO ? 'Edit Purchase Order' : 'Create Purchase Order'}</h2>
          <button
            onClick={() => {
              setShowPOForm(false);
              setEditingPO(null);
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">PO Number (Auto if empty)</label>
              <input
                type="text"
                value={poForm.poNumber}
                onChange={(e) => setPOForm({...poForm, poNumber: e.target.value})}
                placeholder="Leave empty for auto-generation"
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Date</label>
              <input
                type="date"
                value={poForm.date}
                onChange={(e) => setPOForm({...poForm, date: e.target.value})}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Vendor *</label>
              <select
                value={poForm.vendor}
                onChange={(e) => setPOForm({...poForm, vendor: e.target.value})}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              >
                <option value="">Select Vendor</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Project *</label>
              <select
                value={poForm.project}
                onChange={(e) => setPOForm({...poForm, project: e.target.value})}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              >
                <option value="">Select Project</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Delivery Date</label>
              <input
                type="date"
                value={poForm.deliveryDate}
                onChange={(e) => setPOForm({...poForm, deliveryDate: e.target.value})}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Payment Terms</label>
              <select
                value={poForm.terms}
                onChange={(e) => setPOForm({...poForm, terms: e.target.value})}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="Net 7">Net 7 Days</option>
                <option value="Net 15">Net 15 Days</option>
                <option value="Net 30">Net 30 Days</option>
                <option value="Net 45">Net 45 Days</option>
                <option value="Net 60">Net 60 Days</option>
                <option value="Net 90">Net 90 Days</option>
                <option value="Due on Receipt">Due on Receipt</option>
                <option value="Cash on Delivery">Cash on Delivery (COD)</option>
                <option value="50% Advance, 50% on Delivery">50% Advance, 50% on Delivery</option>
                <option value="100% Advance">100% Advance Payment</option>
                <option value="Letter of Credit">Letter of Credit (LC)</option>
                <option value="2/10 Net 30">2/10 Net 30 (2% discount if paid within 10 days)</option>
                <option value="Custom">Custom Terms</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Delivery Address</label>
            <textarea
              value={poForm.deliveryAddress}
              onChange={(e) => setPOForm({...poForm, deliveryAddress: e.target.value})}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
              rows="2"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Line Items</h3>
              <button
                onClick={addItem}
                className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 flex items-center gap-1 text-sm"
              >
                <Plus size={16} />
                Add Item
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium border">Description</th>
                    <th className="px-2 py-2 text-left text-xs font-medium border w-20">Qty</th>
                    <th className="px-2 py-2 text-left text-xs font-medium border w-24">Unit</th>
                    <th className="px-2 py-2 text-left text-xs font-medium border w-28">Rate (₹)</th>
                    <th className="px-2 py-2 text-left text-xs font-medium border w-20">GST %</th>
                    <th className="px-2 py-2 text-left text-xs font-medium border w-32">Rate (Incl. GST)</th>
                    <th className="px-2 py-2 text-left text-xs font-medium border w-32">Total</th>
                    <th className="px-2 py-2 text-left text-xs font-medium border w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {poForm.items.map((item, index) => {
                    const rateInclGST = item.rate + (item.rate * item.gst / 100);
                    return (
                      <tr key={item.id}>
                        <td className="px-2 py-2 border">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => {
                              const newItems = [...poForm.items];
                              newItems[index] = {...item, description: e.target.value};
                              setPOForm({...poForm, items: newItems});
                            }}
                            className="w-full p-1 border rounded text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none mb-1"
                            placeholder="Main description"
                          />
                          <textarea
                            value={item.subDescription || ''}
                            onChange={(e) => {
                              const newItems = [...poForm.items];
                              newItems[index] = {...item, subDescription: e.target.value};
                              setPOForm({...poForm, items: newItems});
                            }}
                            className="w-full p-1 border rounded text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
                            placeholder="Sub description (optional)"
                            rows="2"
                          />
                        </td>
                        <td className="px-2 py-2 border">
                          <input
                            type="number"
                            value={item.quantity === 0 ? '' : item.quantity}
                            onChange={(e) => {
                              const val = e.target.value;
                              const newItems = [...poForm.items];
                              newItems[index] = {...item, quantity: val === '' ? 0 : parseFloat(val)};
                              setPOForm({...poForm, items: newItems});
                            }}
                            className="w-full p-1 border rounded text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                            min="0"
                            step="0.01"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-2 py-2 border">
                          <select
                            value={item.unit}
                            onChange={(e) => {
                              const newItems = [...poForm.items];
                              newItems[index] = {...item, unit: e.target.value};
                              setPOForm({...poForm, items: newItems});
                            }}
                            className="w-full p-1 border rounded text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                          >
                            <option>Nos</option>
                            <option>Kg</option>
                            <option>Ton</option>
                            <option>Sq.Ft</option>
                            <option>Sq.M</option>
                            <option>Box</option>
                            <option>Ltr</option>
                            <option>Mtr</option>
                          </select>
                        </td>
                        <td className="px-2 py-2 border">
                          <input
                            type="number"
                            value={item.rate === 0 ? '' : parseFloat(item.rate.toFixed(4))}
                            onChange={(e) => {
                              const val = e.target.value;
                              const newItems = [...poForm.items];
                              newItems[index] = {...item, rate: val === '' ? 0 : parseFloat(val)};
                              setPOForm({...poForm, items: newItems});
                            }}
                            className="w-full p-1 border rounded text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none bg-blue-50"
                            min="0"
                            step="0.0001"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-2 py-2 border">
                          <input
                            type="number"
                            value={item.gst === 0 ? '' : item.gst}
                            onChange={(e) => {
                              const val = e.target.value;
                              const newItems = [...poForm.items];
                              newItems[index] = {...item, gst: val === '' ? 0 : parseFloat(val)};
                              setPOForm({...poForm, items: newItems});
                            }}
                            className="w-full p-1 border rounded text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                            min="0"
                            max="100"
                            step="0.01"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-2 py-2 border">
                          <input
                            type="number"
                            value={item.rate === 0 ? '' : parseFloat((item.rate * (1 + item.gst / 100)).toFixed(2))}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') {
                                const newItems = [...poForm.items];
                                newItems[index] = {...item, rate: 0};
                                setPOForm({...poForm, items: newItems});
                                return;
                              }
                              
                              const newRateInclGST = parseFloat(val);
                              if (isNaN(newRateInclGST)) return;
                              
                              // Calculate base rate from inclusive rate WITHOUT rounding
                              // This maintains precision: 6 / 1.18 = 5.084745... (stored as-is)
                              const gstMultiplier = 1 + (item.gst / 100);
                              const newBaseRate = newRateInclGST / gstMultiplier;
                              
                              const newItems = [...poForm.items];
                              newItems[index] = {...item, rate: newBaseRate};
                              setPOForm({...poForm, items: newItems});
                            }}
                            className="w-full p-1 border rounded text-sm focus:ring-1 focus:ring-green-500 focus:outline-none bg-green-50"
                            min="0"
                            step="0.01"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-2 py-2 border text-right font-medium text-sm">
                          ₹{calculateItemTotal(item).toFixed(2)}
                        </td>
                        <td className="px-2 py-2 border text-center">
                          <button
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₹{totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total GST:</span>
                  <span className="font-medium">₹{totals.totalGST.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t-2 pt-2">
                  <span>Grand Total:</span>
                  <span>₹{totals.grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Prepared By</label>
              <input
                type="text"
                value={poForm.preparedBy}
                onChange={(e) => setPOForm({...poForm, preparedBy: e.target.value})}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Approved By</label>
              <input
                type="text"
                value={poForm.approvedBy}
                onChange={(e) => setPOForm({...poForm, approvedBy: e.target.value})}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes / Terms & Conditions</label>
            <textarea
              value={poForm.notes}
              onChange={(e) => setPOForm({...poForm, notes: e.target.value})}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
              rows="3"
              placeholder="Enter any additional terms, conditions, or notes..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white">
            <button
              onClick={() => {
                setShowPOForm(false);
                setEditingPO(null);
              }}
              className="px-6 py-2 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={savePO}
              disabled={!poForm.vendor || !poForm.project}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Save size={18} />
              {editingPO ? 'Update' : 'Save'} Purchase Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================= DASHBOARD VIEW ================= */
const DashboardView = ({ purchaseOrders, vendors, projects, setActiveView, previewPO, printPO, downloadPDF, openPOForm, deletePO, darkMode }) => {
  const totalValue = purchaseOrders.reduce((s, p) => s + (p.grandTotal || 0), 0);
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border'} rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wide`}>Purchase Orders</p>
              <p className={`text-4xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mt-2`}>{purchaseOrders.length}</p>
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'} mt-1`}>Total orders issued</p>
            </div>
            <div className={`${darkMode ? 'bg-blue-900' : 'bg-blue-50'} p-3 rounded-lg`}>
              <FileText className={`${darkMode ? 'text-blue-400' : 'text-blue-600'}`} size={28} />
            </div>
          </div>
        </div>
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border'} rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wide`}>Total Value</p>
              <p className={`text-4xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mt-2`}>₹{(totalValue / 100000).toFixed(2)}L</p>
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'} mt-1`}>Cumulative PO value</p>
            </div>
            <div className={`${darkMode ? 'bg-green-900' : 'bg-green-50'} p-3 rounded-lg`}>
              <TrendingUp className={`${darkMode ? 'text-green-400' : 'text-green-600'}`} size={28} />
            </div>
          </div>
        </div>
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border'} rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wide`}>Active Vendors</p>
              <p className={`text-4xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mt-2`}>{vendors.length}</p>
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'} mt-1`}>Registered vendors</p>
            </div>
            <div className={`${darkMode ? 'bg-purple-900' : 'bg-purple-50'} p-3 rounded-lg`}>
              <Users className={`${darkMode ? 'text-purple-400' : 'text-purple-600'}`} size={28} />
            </div>
          </div>
        </div>
      </div>

      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border'} rounded-lg shadow-sm p-6`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Recent Purchase Orders</h2>
          <button
            onClick={() => setActiveView('pos')}
            className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} text-sm font-medium transition-colors`}
          >
            View All →
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-b`}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>PO Number</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Date</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Vendor</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Amount</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`${darkMode ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
              {purchaseOrders
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 5)
                .map(po => {
                const vendor = vendors.find(v => v.id === po.vendor);
                return (
                  <tr key={po.id} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
                    <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{po.poNumber}</td>
                    <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{new Date(po.date).toLocaleDateString()}</td>
                    <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{vendor?.name || 'N/A'}</td>
                    <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>₹{po.grandTotal.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <button onClick={() => previewPO(po)} className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} transition-colors`} title="Preview">
                          <Eye size={16} />
                        </button>
                        <button onClick={() => downloadPDF(po)} className={`${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-800'} transition-colors`} title="Download PDF">
                          <Download size={16} />
                        </button>
                        <button onClick={() => printPO(po)} className={`${darkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-800'} transition-colors`} title="Print">
                          <Printer size={16} />
                        </button>
                        <button onClick={() => openPOForm(po)} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'} transition-colors`} title="Edit">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => deletePO(po.id)} className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'} transition-colors`} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {purchaseOrders.length === 0 && (
            <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No purchase orders yet. Create your first PO!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ================= VENDORS VIEW ================= */
const VendorsView = ({ vendors, openVendorForm, deleteVendor, darkMode }) => (
  <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border'} rounded-lg shadow-sm p-6`}>
    <div className="flex justify-between items-center mb-6">
      <h2 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Vendors</h2>
      <button
        onClick={() => openVendorForm()}
        className={`${darkMode ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 py-2 rounded-md flex items-center gap-2 shadow-sm transition-all hover:shadow-md font-medium`}
      >
        <Plus size={18} />
        Add Vendor
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-b`}>
          <tr>
            <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>ID</th>
            <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Name</th>
            <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Contact</th>
            <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Phone</th>
            <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Email</th>
            <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>GST</th>
            <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Actions</th>
          </tr>
        </thead>
        <tbody className={`${darkMode ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
          {vendors.map(vendor => (
            <tr key={vendor.id} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{vendor.id}</td>
              <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{vendor.name}</td>
              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{vendor.contact}</td>
              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{vendor.phone}</td>
              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{vendor.email}</td>
              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{vendor.gst}</td>
              <td className="px-4 py-3 text-sm">
                <div className="flex gap-2">
                  <button
                    onClick={() => openVendorForm(vendor)}
                    className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} transition-colors`}
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => deleteVendor(vendor.id)}
                    className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'} transition-colors`}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {vendors.length === 0 && (
        <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No vendors yet. Add your first vendor!
        </div>
      )}
    </div>
  </div>
);

/* ================= PROJECTS VIEW ================= */
const ProjectsView = ({ projects, openProjectForm, deleteProject, darkMode }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h2 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Projects</h2>
      <button
        onClick={() => openProjectForm()}
        className={`${darkMode ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 py-2 rounded-md flex items-center gap-2 shadow-sm transition-all hover:shadow-md font-medium`}
      >
        <Plus size={18} />
        Add Project
      </button>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
      {projects.map(project => {
        const percentage = (project.spent / project.budget) * 100;
        return (
          <div key={project.id} className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border'} rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow`}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{project.name}</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>Code: {project.code}</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-0.5`}>ID: {project.id}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openProjectForm(project)}
                  className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} transition-colors`}
                  title="Edit"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => deleteProject(project.id)}
                  className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'} transition-colors`}
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Budget:</span>
                <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>₹{project.budget.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Spent:</span>
                <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>₹{project.spent.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Remaining:</span>
                <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>₹{(project.budget - project.spent).toLocaleString('en-IN')}</span>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Progress:</span>
                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{percentage.toFixed(1)}%</span>
                </div>
                <div className={`w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2.5 overflow-hidden`}>
                  <div
                    className={`h-2.5 rounded-full transition-all ${
                      percentage < 50 ? 'bg-green-500' :
                      percentage < 80 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {projects.length === 0 && (
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border'} rounded-lg shadow-sm p-12 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No projects yet. Add your first project!
      </div>
    )}
  </div>
);

/* ================= POS VIEW ================= */
const POsView = ({ purchaseOrders, vendors, projects, previewPO, printPO, downloadPDF, openPOForm, deletePO, darkMode }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterDate, setFilterDate] = React.useState('');
  const [minAmount, setMinAmount] = React.useState('');
  const [maxAmount, setMaxAmount] = React.useState('');

  const filteredPOs = purchaseOrders
    .filter(po => {
      const vendor = vendors.find(v => v.id === po.vendor);
      const project = projects.find(p => p.id === po.project);
      
      // Search filter
      const matchesSearch = searchTerm === '' || 
        po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (vendor?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Date filter
      const matchesDate = filterDate === '' || po.date === filterDate;
      
      // Amount filter
      const matchesMinAmount = minAmount === '' || po.grandTotal >= parseFloat(minAmount);
      const matchesMaxAmount = maxAmount === '' || po.grandTotal <= parseFloat(maxAmount);
      
      return matchesSearch && matchesDate && matchesMinAmount && matchesMaxAmount;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border'} rounded-lg shadow-sm p-6`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>All Purchase Orders</h2>
      </div>
      
      {/* Search and Filter Section */}
      <div className="mb-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className={`block text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-1.5`}>Search</label>
            <input
              type="text"
              placeholder="Search by PO, Vendor, Project..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white'}`}
            />
          </div>
          
          <div>
            <label className={`block text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-1.5`}>Filter by Date</label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'}`}
            />
          </div>
          
          <div>
            <label className={`block text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-1.5`}>Min Amount (₹)</label>
            <input
              type="number"
              placeholder="Minimum"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white'}`}
            />
          </div>
          
          <div>
            <label className={`block text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-1.5`}>Max Amount (₹)</label>
            <input
              type="number"
              placeholder="Maximum"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white'}`}
            />
          </div>
        </div>
        
        {(searchTerm || filterDate || minAmount || maxAmount) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterDate('');
              setMinAmount('');
              setMaxAmount('');
            }}
            className={`text-sm font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
          >
            Clear all filters
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-b`}>
            <tr>
              <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>PO Number</th>
              <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Date</th>
              <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Vendor</th>
              <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Project</th>
              <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Amount</th>
              <th className={`px-4 py-3 text-left text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Actions</th>
            </tr>
          </thead>
          <tbody className={`${darkMode ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
            {filteredPOs.map(po => {
              const vendor = vendors.find(v => v.id === po.vendor);
              const project = projects.find(p => p.id === po.project);
              return (
                <tr key={po.id} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
                  <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{po.poNumber}</td>
                  <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{new Date(po.date).toLocaleDateString()}</td>
                  <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{vendor?.name || 'N/A'}</td>
                  <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{project?.name || 'N/A'}</td>
                  <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>₹{po.grandTotal.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => previewPO(po)}
                        className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} transition-colors`}
                        title="Preview PO"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => downloadPDF(po)}
                        className={`${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-800'} transition-colors`}
                        title="Download PDF"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        onClick={() => printPO(po)}
                        className={`${darkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-800'} transition-colors`}
                        title="Print PO"
                      >
                        <Printer size={16} />
                      </button>
                      <button
                        onClick={() => openPOForm(po)}
                        className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'} transition-colors`}
                        title="Edit PO"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => deletePO(po.id)}
                        className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'} transition-colors`}
                        title="Delete PO"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredPOs.length === 0 && (
          <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {searchTerm || filterDate || minAmount || maxAmount 
              ? 'No purchase orders match your filters.' 
              : 'No purchase orders yet. Create your first PO!'}
          </div>
        )}
      </div>
    </div>
  );
};

/* ================= MAIN APP COMPONENT ================= */
function App({ userId }) {
  const [activeView, setActiveView] = useState('dashboard');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showPOForm, setShowPOForm] = useState(false);
  const [showCompanyEditor, setShowCompanyEditor] = useState(false);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingPO, setEditingPO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  const [companyProfile, setCompanyProfile] = useState({
    name: 'BM ADISON PROJECTS',
    address: 'Plot No. 45, Industrial Estate, Guwahati, Assam - 781001',
    phone: '+91-361-2345678',
    email: 'info@bmadison.com',
    gst: '18AABCU9603R1ZP',
    pan: 'AABCU9603R'
  });

  const [tempCompanyProfile, setTempCompanyProfile] = useState({...companyProfile});

  // Load data from Supabase on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [posData, vendorsData, projectsData] = await Promise.all([
          fetchPurchaseOrders(userId),
          fetchVendors(userId),
          fetchProjects(userId)
        ]);

        // Transform Supabase data to match app format
        const transformedPOs = posData.map(po => ({
          id: po.id,
          poNumber: po.po_number,
          date: po.date,
          vendor: po.vendor,
          project: po.project,
          deliveryDate: po.delivery_date,
          deliveryAddress: po.delivery_address,
          terms: po.terms,
          items: po.items,
          notes: po.notes,
          preparedBy: po.prepared_by,
          approvedBy: po.approved_by,
          subtotal: parseFloat(po.subtotal),
          totalGST: parseFloat(po.total_gst),
          grandTotal: parseFloat(po.grand_total)
        }));

        setPurchaseOrders(transformedPOs);
        setVendors(vendorsData);
        setProjects(projectsData);

        // Add sample data if no data exists
        if (vendorsData.length === 0) {
          const sampleVendor = {
            id: genBMAP('VEN'),
            name: 'Premium Steel Suppliers Ltd.',
            contact: 'John Smith',
            phone: '+91-9876543210',
            email: 'john@premiumsteel.com',
            gst: '07AABCU9603R1ZM',
            address: '123 Industrial Area, Guwahati'
          };
          await saveVendor(userId, sampleVendor);
          setVendors([sampleVendor]);
        }

        if (projectsData.length === 0) {
          const sampleProject = {
            id: genBMAP('PRJ'),
            name: 'Skyline Tower - Phase 1',
            code: 'SKY-001',
            budget: 50000000,
            spent: 32000000
          };
          await saveProject(userId, sampleProject);
          setProjects([sampleProject]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
      
      // Load company profile
      try {
        const profileData = await fetchCompanyProfile(userId);
        if (profileData) {
          setCompanyProfile(profileData);
          setTempCompanyProfile(profileData);
        }
      } catch (error) {
        console.error('Error loading company profile:', error);
      }
      
      setLoading(false);
    };

    loadData();
  }, [userId]);

  const [poForm, setPOForm] = useState({
    poNumber: '',
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    project: '',
    deliveryDate: '',
    deliveryAddress: '',
    terms: 'Net 30',
    items: [{
      id: crypto.randomUUID(),
      description: '',
      quantity: 1,
      unit: 'Nos',
      rate: 0,
      gst: 18
    }],
    notes: '',
    preparedBy: '',
    approvedBy: ''
  });

  const [vendorForm, setVendorForm] = useState({
    name: '',
    contact: '',
    phone: '',
    email: '',
    gst: '',
    address: ''
  });

  const [projectForm, setProjectForm] = useState({
    name: '',
    code: '',
    budget: 0,
    spent: 0
  });

  const openCompanyEditor = () => {
    setTempCompanyProfile({...companyProfile});
    setShowCompanyEditor(true);
  };

  const handleSaveCompanyProfile = async () => {
    try {
      await dbSaveCompanyProfile(userId, tempCompanyProfile);
      setCompanyProfile({...tempCompanyProfile});
      setShowCompanyEditor(false);
    } catch (error) {
      console.error('Error saving company profile:', error);
      alert('Failed to save company profile. Please try again.');
    }
  };

  const openVendorForm = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setVendorForm({...vendor});
    } else {
      setEditingVendor(null);
      setVendorForm({
        name: '',
        contact: '',
        phone: '',
        email: '',
        gst: '',
        address: ''
      });
    }
    setShowVendorForm(true);
  };

  const handleSaveVendor = async () => {
    const vendorData = editingVendor 
      ? {...vendorForm, id: editingVendor.id}
      : {...vendorForm, id: genBMAP('VEN')};
    
    try {
      // Save to Supabase
      await saveVendor(userId, vendorData);
      
      // Update local state
      if (editingVendor) {
        setVendors(prev => prev.map(v => v.id === editingVendor.id ? vendorData : v));
      } else {
        setVendors(prev => [...prev, vendorData]);
      }
      
      setShowVendorForm(false);
      setEditingVendor(null);
    } catch (error) {
      console.error('Error saving vendor:', error);
      alert('Failed to save vendor. Please try again.');
    }
  };

  const deleteVendor = async (id) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        // Delete from Supabase
        await dbDeleteVendor(id);
        
        // Update local state
        setVendors(prev => prev.filter(v => v.id !== id));
      } catch (error) {
        console.error('Error deleting vendor:', error);
        alert('Failed to delete vendor. Please try again.');
      }
    }
  };

  const openProjectForm = (project = null) => {
    if (project) {
      setEditingProject(project);
      setProjectForm({...project});
    } else {
      setEditingProject(null);
      setProjectForm({
        name: '',
        code: '',
        budget: 0,
        spent: 0
      });
    }
    setShowProjectForm(true);
  };

  const handleSaveProject = async () => {
    const projectData = editingProject
      ? {...projectForm, id: editingProject.id}
      : {...projectForm, id: genBMAP('PRJ')};
    
    try {
      // Save to Supabase
      await saveProject(userId, projectData);
      
      // Update local state
      if (editingProject) {
        setProjects(prev => prev.map(p => p.id === editingProject.id ? projectData : p));
      } else {
        setProjects(prev => [...prev, projectData]);
      }
      
      setShowProjectForm(false);
      setEditingProject(null);
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project. Please try again.');
    }
  };

  const deleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        // Delete from Supabase
        await dbDeleteProject(id);
        
        // Update local state
        setProjects(prev => prev.filter(p => p.id !== id));
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Failed to delete project. Please try again.');
      }
    }
  };

  const openPOForm = (po = null) => {
    if (po) {
      setEditingPO(po);
      setPOForm({...po});
    } else {
      setEditingPO(null);
      setPOForm({
        poNumber: '',
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        project: '',
        deliveryDate: '',
        deliveryAddress: '',
        terms: 'Net 30',
        items: [{
          id: crypto.randomUUID(),
          description: '',
          quantity: 1,
          unit: 'Nos',
          rate: 0,
          gst: 18
        }],
        notes: '',
        preparedBy: '',
        approvedBy: ''
      });
    }
    setShowPOForm(true);
  };

  const addItem = () => {
    setPOForm(prev => ({
      ...prev,
      items: [...prev.items, {
        id: crypto.randomUUID(),
        description: '',
        subDescription: '',
        quantity: 1,
        unit: 'Nos',
        rate: 0,
        gst: 18
      }]
    }));
  };

  const removeItem = (index) => {
    setPOForm(prev => {
      const newItems = prev.items.filter((_, i) => i !== index);
      // If all items removed, add one empty item
      if (newItems.length === 0) {
        return {
          ...prev,
          items: [{
            id: crypto.randomUUID(),
            description: '',
            quantity: 1,
            unit: 'Nos',
            rate: 0,
            gst: 18
          }]
        };
      }
      return {...prev, items: newItems};
    });
  };

  const savePO = async () => {
    const totals = calculateTotals(poForm.items);
    const poData = {
      ...poForm,
      id: editingPO ? editingPO.id : genBMAP('PO'),
      poNumber: poForm.poNumber || genBMAP('PO'),
      ...totals
    };
    
    try {
      // Save to Supabase
      await savePurchaseOrder(userId, poData);
      
      // Update local state
      if (editingPO) {
        setPurchaseOrders(prev => prev.map(p => p.id === editingPO.id ? poData : p));
      } else {
        setPurchaseOrders(prev => [...prev, poData]);
      }
      
      setShowPOForm(false);
      setEditingPO(null);
    } catch (error) {
      console.error('Error saving PO:', error);
      alert('Failed to save purchase order. Please try again.');
    }
  };

  const deletePO = async (id) => {
    if (window.confirm('Are you sure you want to delete this purchase order?')) {
      try {
        // Delete from Supabase
        await dbDeletePO(id);
        
        // Update local state
        setPurchaseOrders(prev => prev.filter(p => p.id !== id));
      } catch (error) {
        console.error('Error deleting PO:', error);
        alert('Failed to delete purchase order. Please try again.');
      }
    }
  };

  const previewPO = (po) => {
    const previewWindow = window.open('', '_blank');
    const vendor = vendors.find(v => v.id === po.vendor);
    const project = projects.find(p => p.id === po.project);
    
    // Extract state codes from GST numbers (first 2 digits)
    const companyStateCode = companyProfile.gst.substring(0, 2);
    const vendorStateCode = vendor?.gst ? vendor.gst.substring(0, 2) : companyStateCode;
    const isInterState = companyStateCode !== vendorStateCode;
    
    const htmlContent = generatePOHTML(po, vendor, project, isInterState);
    
    previewWindow.document.write(htmlContent);
    previewWindow.document.close();
    previewWindow.focus();
  };

  const printPO = (po) => {
    // Create a hidden iframe for printing (no new tab)
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    
    const vendor = vendors.find(v => v.id === po.vendor);
    const project = projects.find(p => p.id === po.project);
    
    // Extract state codes from GST numbers (first 2 digits)
    const companyStateCode = companyProfile.gst.substring(0, 2);
    const vendorStateCode = vendor?.gst ? vendor.gst.substring(0, 2) : companyStateCode;
    const isInterState = companyStateCode !== vendorStateCode;
    
    const htmlContent = generatePOHTML(po, vendor, project, isInterState);
    
    iframe.contentDocument.write(htmlContent);
    iframe.contentDocument.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      // Clean up after printing
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
  };

  const downloadPDF = (po) => {
    // Open in new window and trigger print to PDF
    const printWindow = window.open('', '_blank');
    const vendor = vendors.find(v => v.id === po.vendor);
    const project = projects.find(p => p.id === po.project);
    
    // Extract state codes from GST numbers (first 2 digits)
    const companyStateCode = companyProfile.gst.substring(0, 2);
    const vendorStateCode = vendor?.gst ? vendor.gst.substring(0, 2) : companyStateCode;
    const isInterState = companyStateCode !== vendorStateCode;
    
    const htmlContent = generatePOHTML(po, vendor, project, isInterState);
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Automatically trigger print dialog
    // User can select "Save as PDF" and filename will be the PO number
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  // Helper function to generate PO HTML (used by both preview and print)
  const generatePOHTML = (po, vendor, project, isInterState) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Purchase Order - ${po.poNumber}</title>
        <style>
          @page { 
            size: A4; 
            margin: 0;
          }
          * { 
            margin: 0; 
            padding: 0; 
            box-sizing: border-box; 
          }
          body { 
            font-family: Arial, sans-serif;
            font-size: 7.5pt;
            line-height: 1.15;
            color: #000;
            background: #fff;
          }
          .page {
            width: 210mm;
            height: 297mm;
            padding: 6mm;
            margin: 0 auto;
            border: 2px solid #333;
            position: relative;
          }
          
          /* HEADER SECTION */
          .header {
            text-align: center;
            padding: 4px 0;
            border-bottom: 2px solid #333;
            margin-bottom: 4px;
            position: relative;
          }
          .header-title {
            font-size: 10pt;
            font-weight: bold;
            letter-spacing: 1px;
          }
          
          /* CONSIGNEE BILLING - FIXED POSITIONING */
          .consignee-section {
            position: absolute;
            right: 6mm;
            top: 18mm;
            width: 85mm;
            font-size: 6.5pt;
            line-height: 1.25;
            z-index: 10;
          }
          .consignee-title {
            font-weight: bold;
            margin-bottom: 1px;
          }
          
          /* INFO BOXES */
          .info-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 4px;
            margin-bottom: 4px;
            margin-top: 48mm;
          }
          .info-box {
            border: 1px solid #333;
            padding: 3px;
            font-size: 6.5pt;
          }
          .info-box-title {
            font-weight: bold;
            font-size: 7pt;
            margin-bottom: 1px;
          }
          .info-line {
            margin: 0.5px 0;
            line-height: 1.2;
          }
          .info-label {
            font-weight: bold;
          }
          
          /* VENDOR DETAILS */
          .vendor-section {
            border: 1px solid #333;
            padding: 3px;
            margin-bottom: 3px;
            font-size: 6.5pt;
          }
          
          /* ITEMS TABLE */
          .items-note {
            font-size: 6pt;
            margin: 2px 0;
            font-style: italic;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 3px 0;
            font-size: 6.5pt;
          }
          .items-table th,
          .items-table td {
            border: 1px solid #333;
            padding: 2px;
            text-align: center;
          }
          .items-table th {
            background: #e0e0e0;
            color: #000;
            font-weight: bold;
            font-size: 6.5pt;
          }
          .items-table .desc-col {
            text-align: left;
          }
          
          /* TOTALS */
          .totals-section {
            margin: 3px 0;
            text-align: right;
            font-size: 7pt;
          }
          .grand-total {
            background: #d3d3d3;
            color: #000;
            display: inline-block;
            padding: 2px 8px;
            margin-top: 2px;
            font-weight: bold;
          }
          
          /* DELIVERY ADDRESS */
          .delivery-section {
            border: 1px solid #333;
            padding: 3px;
            margin: 3px 0;
            font-size: 6.5pt;
          }
          
          /* FOOTER TERMS */
          .terms-section {
            margin: 3px 0;
            font-size: 6.5pt;
          }
          .terms-row {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 2px;
            margin: 2px 0;
          }
          .term-box {
            border: 1px solid #333;
            padding: 2px;
          }
          
          /* CONSIGNEE GST */
          .gst-section {
            margin: 3px 0;
            font-size: 6.5pt;
          }
          .gst-note {
            font-style: italic;
            margin: 1px 0;
          }
          
          /* SIGNATURES */
          .signatures {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 4px;
            margin-top: 6px;
            font-size: 6.5pt;
          }
          .sig-box {
            text-align: center;
            padding-top: 15px;
          }
          .sig-label {
            font-weight: bold;
            border-top: 1px solid #333;
            padding-top: 2px;
            margin-top: 15px;
          }
          
          /* AUTHORIZATION NOTE */
          .auth-note {
            text-align: center;
            font-size: 6pt;
            margin-top: 3px;
            font-weight: bold;
          }
          
          /* FOOTER */
          .system-footer {
            position: absolute;
            bottom: 3mm;
            left: 6mm;
            right: 6mm;
            text-align: center;
            font-size: 5pt;
            color: #666;
            border-top: 1px solid #ddd;
            padding-top: 2mm;
          }
          
          @media print {
            body {
              margin: 0;
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <!-- HEADER -->
          <div class="header">
            <div class="header-title">PURCHASE ORDER</div>
          </div>
          
          <!-- LOGO - LEFT: Registered Office, TOP: Consignee -->
          <div style="position: absolute; left: 6mm; top: 18mm;">
            <img src="${process.env.PUBLIC_URL}/bmap_logo_1.png" alt="BM Logo" style="height: 60px; width: auto; max-width: 80px; object-fit: contain;" onerror="this.style.display='none'">
          </div>
          
          <!-- CONSIGNEE BILLING INFO (FIXED TOP RIGHT) -->
          <div class="consignee-section">
            <div class="consignee-title">Consignee / Billing Address:</div>
            <div>${companyProfile.name}</div>
            <div>${companyProfile.address}</div>
            <div>Email: ${companyProfile.email}</div>
            <div>Contact Person: ${po.preparedBy || 'N/A'}</div>
            <div>Contact No.: ${companyProfile.phone}</div>
            <div>GST No.: ${companyProfile.gst}</div>
          </div>
          
          <!-- INFO BOXES -->
          <div class="info-row">
            <div class="info-box">
              <div class="info-box-title">Registered Office:</div>
              <div class="info-line"><strong>${companyProfile.name}</strong></div>
              <div class="info-line">${companyProfile.address}</div>
              <div class="info-line">Email: ${companyProfile.email}</div>
              <div class="info-line">Phone: ${companyProfile.phone}</div>
              <div class="info-line">GST: ${companyProfile.gst}</div>
              <div class="info-line">PAN: ${companyProfile.pan}</div>
            </div>
            <div class="info-box">
              <div class="info-line"><span class="info-label">PO No.:</span> ${po.poNumber}</div>
              <div class="info-line"><span class="info-label">Ref No.:</span> ${po.project ? project?.code : 'N/A'}</div>
              <div class="info-line"><span class="info-label">Date:</span> ${new Date(po.date).toLocaleDateString('en-GB')}</div>
              <div class="info-line"><span class="info-label">P.A. No.:</span> ${po.poNumber}</div>
              <div style="border-top: 1px solid #333; margin: 3px 0; padding-top: 3px; text-align: center;">
                <img src="https://bwipjs-api.metafloor.com/?bcid=code128&text=${encodeURIComponent(po.poNumber)}&scale=2&height=8&includetext&textsize=10" alt="Barcode" style="max-width: 70%; height: auto;" />
              </div>
            </div>
          </div>
          
          <!-- VENDOR DETAILS -->
          <div class="vendor-section">
            <div style="font-weight: bold; margin-bottom: 4px;">VENDOR'S NAME & ADDRESS:</div>
            <div>${vendor?.name || 'N/A'}</div>
            <div>${vendor?.address || ''}</div>
            <div>Contact Person: ${vendor?.contact || ''} | ${vendor?.phone || ''}</div>
            <div>Vendor ID: ${vendor?.id || ''}</div>
            <div>GST No.: ${vendor?.gst || ''}</div>
          </div>
          
          <!-- KIND ATTENTION NOTE -->
          <div class="items-note">
            Kind Attention: Goods received are subject to our terms and conditions of order and Please arrange to supply accordingly.
          </div>
          
          <!-- ITEMS TABLE -->
          <table class="items-table">
            <thead>
              <tr>
                <th style="width: 30px;">S. No.</th>
                <th>Material Description</th>
                <th style="width: 50px;">UoM</th>
                <th style="width: 50px;">Qty</th>
                <th style="width: 60px;">Rate</th>
                <th style="width: 40px;">GST %</th>
                <th style="width: 70px;">Rate (Incl. GST)</th>
                <th style="width: 90px;">Total Value</th>
              </tr>
            </thead>
            <tbody>
              ${po.items.map((item, index) => {
                const rateInclGST = item.rate * (1 + item.gst / 100);
                const itemSubtotal = item.quantity * item.rate;
                const itemGST = itemSubtotal * item.gst / 100;
                return `
                <tr>
                  <td>${index + 1}</td>
                  <td class="desc-col">
                    <div style="font-weight: bold;">${item.description}</div>
                    ${item.subDescription ? `<div style="font-size: 5.5pt; color: #555; margin-top: 1px; white-space: pre-wrap;">${item.subDescription}</div>` : ''}
                  </td>
                  <td>${item.unit}</td>
                  <td>${item.quantity}</td>
                  <td>${item.rate.toFixed(2)}</td>
                  <td>${item.gst}%</td>
                  <td>${rateInclGST.toFixed(2)}</td>
                  <td>${(itemSubtotal + itemGST).toFixed(2)}</td>
                </tr>
              `;
              }).join('')}
              <tr>
                <td colspan="7" style="text-align: right; font-weight: bold;">Subtotal (Excl. GST)</td>
                <td style="font-weight: bold;">${po.subtotal.toFixed(2)}</td>
              </tr>
              ${isInterState ? `
              <tr>
                <td colspan="7" style="text-align: right;">IGST @ ${po.items[0]?.gst || 18}%</td>
                <td>${po.totalGST.toFixed(2)}</td>
              </tr>
              ` : `
              <tr>
                <td colspan="7" style="text-align: right;">CGST @ ${(po.items[0]?.gst || 18) / 2}%</td>
                <td>${(po.totalGST / 2).toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="7" style="text-align: right;">SGST @ ${(po.items[0]?.gst || 18) / 2}%</td>
                <td>${(po.totalGST / 2).toFixed(2)}</td>
              </tr>
              `}
              <tr>
                <td colspan="7" style="text-align: right; font-weight: bold; border-top: 2px solid #333;">Total GST</td>
                <td style="font-weight: bold; border-top: 2px solid #333;">${po.totalGST.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
          
          <!-- GRAND TOTAL -->
          <div class="totals-section">
            <div class="grand-total">Grand Total: ₹ ${po.grandTotal.toFixed(2)}</div>
          </div>
          
          <!-- DELIVERY ADDRESS -->
          <div class="delivery-section">
            <div style="font-weight: bold;">Delivery Address:</div>
            <div>${po.deliveryAddress || project?.name || 'N/A'}</div>
          </div>
          
          <!-- TERMS -->
          <div class="terms-section">
            <div class="terms-row">
              <div class="term-box">
                <div><strong>GST Rate:</strong></div>
                <div>GST Included As Above</div>
              </div>
              <div class="term-box">
                <div><strong>P.O. & Tax:</strong></div>
                <div>DELIVERY SCHEDULE: ${po.deliveryDate ? new Date(po.deliveryDate).toLocaleDateString('en-GB') : 'As per contract'}</div>
              </div>
              <div class="term-box">
                <div><strong>Payment Terms:</strong></div>
                <div>${po.terms}</div>
              </div>
            </div>
          </div>
          
          <!-- CONSIGNEE GST -->
          <div class="gst-section">
            <div><strong>Consignee's GST No.:</strong> ${companyProfile.gst}</div>
            <div class="gst-note">Original Tax Invoice have to be provided along with supply of material.</div>
            <div><strong>Consignee's GST No.:</strong> ${companyProfile.gst}</div>
            <div class="gst-note">Yours faithfully,<br>${companyProfile.name}</div>
          </div>
          
          ${po.notes ? `
          <!-- NOTES / TERMS & CONDITIONS -->
          <div style="margin: 4px 0; padding: 3px; border: 1px solid #333; font-size: 6.5pt;">
            <div style="font-weight: bold; margin-bottom: 2px;">Notes / Terms & Conditions:</div>
            <div style="white-space: pre-wrap;">${po.notes}</div>
          </div>
          ` : ''}
          
          <!-- SIGNATURES -->
          <div class="signatures">
            <div class="sig-box">
              <div class="sig-label">Prepared</div>
              <div>${po.preparedBy || ''}</div>
            </div>
            <div class="sig-box">
              <div class="sig-label">Approved</div>
              <div>${po.approvedBy || ''}</div>
            </div>
          </div>
          
          <!-- AUTHORIZATION -->
          <div class="auth-note">AUTHORISED SIGNATORY</div>
          
          <!-- FOOTER NOTE -->
          <div style="text-align: center; font-size: 5.5pt; margin-top: 3px;">
            The order is issued in duplicate please return an extra copy of Purchase Order duly signed as a token of your acceptance.
          </div>
          
          <!-- SYSTEM FOOTER -->
          <div class="system-footer">
            System Generated via Alita 2.1 POMS -- ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(/\//g, '/')} IST
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Loading screen while fetching data
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading your data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header - Responsive Design */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-b'} shadow-sm`}>
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-5 max-w-[1800px]">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3 sm:gap-6 w-full sm:w-auto">
              <img 
                src={`${process.env.PUBLIC_URL}/bmap_logo_1.png`}
                alt="BM Logo" 
                className="h-12 sm:h-16 w-auto object-contain flex-shrink-0"
                onError={(e) => {e.target.style.display='none'}}
              />
              <div className="flex-1 min-w-0">
                <h1 className={`text-lg sm:text-2xl lg:text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} tracking-tight truncate`}>{companyProfile.name}</h1>
                <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1 sm:mt-2 leading-relaxed line-clamp-2`}>{companyProfile.address}</p>
                <div className={`flex flex-wrap gap-2 sm:gap-4 mt-1 sm:mt-2 text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <span className="truncate">{companyProfile.phone}</span>
                  <span className="hidden sm:inline">•</span>
                  <span className="truncate">{companyProfile.email}</span>
                </div>
                <div className={`flex flex-wrap gap-2 sm:gap-4 mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>GST: {companyProfile.gst}</span>
                  <span className="hidden sm:inline">•</span>
                  <span>PAN: {companyProfile.pan}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-center self-end sm:self-auto">
              <button
                onClick={toggleDarkMode}
                className={`${darkMode ? 'text-yellow-400 hover:text-yellow-300 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'} p-2 rounded-md transition-colors`}
                title={darkMode ? 'Light Mode' : 'Dark Mode'}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <button
                onClick={openCompanyEditor}
                className={`${darkMode ? 'text-blue-400 hover:text-blue-300 hover:bg-gray-700' : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'} flex items-center gap-2 text-xs sm:text-sm font-medium transition-colors px-3 sm:px-4 py-2 rounded-md whitespace-nowrap`}
              >
                <Edit2 size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Edit Profile</span>
                <span className="sm:hidden">Edit</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation - Responsive Style */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-b'} overflow-x-auto`}>
        <div className="container mx-auto px-4 sm:px-6 max-w-[1800px]">
          <nav className="flex gap-1 min-w-max sm:min-w-0">
            <button
              onClick={() => setActiveView('dashboard')}
              className={`py-3 sm:py-4 px-4 sm:px-6 text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                activeView === 'dashboard'
                  ? darkMode ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-700' : 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveView('pos')}
              className={`py-3 sm:py-4 px-4 sm:px-6 text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                activeView === 'pos'
                  ? darkMode ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-700' : 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">Purchase Orders</span>
              <span className="sm:hidden">POs</span>
            </button>
            <button
              onClick={() => setActiveView('vendors')}
              className={`py-3 sm:py-4 px-4 sm:px-6 text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                activeView === 'vendors'
                  ? darkMode ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-700' : 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Vendors
            </button>
            <button
              onClick={() => setActiveView('projects')}
              className={`py-3 sm:py-4 px-4 sm:px-6 text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                activeView === 'projects'
                  ? darkMode ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-700' : 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Projects
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8 max-w-[1800px]">
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => openPOForm()}
            className={`w-full sm:w-auto ${darkMode ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-md flex items-center justify-center gap-2 font-medium shadow-sm transition-all hover:shadow-md text-sm sm:text-base`}
          >
            <Plus size={18} className="sm:w-5 sm:h-5" />
            Create Purchase Order
          </button>
        </div>

        {activeView === 'dashboard' && (
          <DashboardView 
            purchaseOrders={purchaseOrders}
            vendors={vendors}
            projects={projects}
            setActiveView={setActiveView}
            previewPO={previewPO}
            printPO={printPO}
            downloadPDF={downloadPDF}
            openPOForm={openPOForm}
            deletePO={deletePO}
            darkMode={darkMode}
          />
        )}
        {activeView === 'pos' && (
          <POsView
            purchaseOrders={purchaseOrders}
            vendors={vendors}
            projects={projects}
            previewPO={previewPO}
            printPO={printPO}
            downloadPDF={downloadPDF}
            openPOForm={openPOForm}
            deletePO={deletePO}
            darkMode={darkMode}
          />
        )}
        {activeView === 'vendors' && (
          <VendorsView
            vendors={vendors}
            openVendorForm={openVendorForm}
            deleteVendor={deleteVendor}
            darkMode={darkMode}
          />
        )}
        {activeView === 'projects' && (
          <ProjectsView
            projects={projects}
            openProjectForm={openProjectForm}
            deleteProject={deleteProject}
            darkMode={darkMode}
          />
        )}
      </div>

      {/* Modals */}
      {showCompanyEditor && (
        <CompanyEditorModal
          tempCompanyProfile={tempCompanyProfile}
          setTempCompanyProfile={setTempCompanyProfile}
          setShowCompanyEditor={setShowCompanyEditor}
          saveCompanyProfile={handleSaveCompanyProfile}
        />
      )}
      {showVendorForm && (
        <VendorFormModal
          editingVendor={editingVendor}
          vendorForm={vendorForm}
          setVendorForm={setVendorForm}
          setShowVendorForm={setShowVendorForm}
          handleSaveVendor={handleSaveVendor}
        />
      )}
      {showProjectForm && (
        <ProjectFormModal
          editingProject={editingProject}
          projectForm={projectForm}
          setProjectForm={setProjectForm}
          setShowProjectForm={setShowProjectForm}
          handleSaveProject={handleSaveProject}
        />
      )}
      {showPOForm && (
        <POFormView
          poForm={poForm}
          setPOForm={setPOForm}
          vendors={vendors}
          projects={projects}
          editingPO={editingPO}
          setShowPOForm={setShowPOForm}
          setEditingPO={setEditingPO}
          savePO={savePO}
          addItem={addItem}
          removeItem={removeItem}
        />
      )}
    </div>
  );
}

export default App;